import React, {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
  ReactNode,
} from 'react';
import { ThemeProvider } from '@mui/material/styles';
import { buildTheme } from '../../theme';
import { applyThemeVariables, ThemeMode } from '../../styles/themeColors';

const STORAGE_KEY = 'themeMode';

interface ThemeModeContextProps {
  mode: ThemeMode;
  toggleMode: () => void;
  setMode: (mode: ThemeMode) => void;
}

const ThemeModeContext = createContext<ThemeModeContextProps | undefined>(
  undefined
);

function getInitialMode(): ThemeMode {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === 'light' || stored === 'dark') return stored;
  return 'dark';
}

/**
 * Owns the active color mode for the whole app: persists it, applies the
 * matching CSS variables to <html>, and provides the corresponding MUI theme.
 * Replaces the static MUI ThemeProvider previously in index.tsx.
 */
export const ThemeModeProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [mode, setModeState] = useState<ThemeMode>(getInitialMode);

  // Runs before paint, so the correct colors are applied without a flash.
  useLayoutEffect(() => {
    applyThemeVariables(mode);
    localStorage.setItem(STORAGE_KEY, mode);
  }, [mode]);

  const setMode = useCallback((next: ThemeMode) => setModeState(next), []);
  const toggleMode = useCallback(
    () => setModeState((prev) => (prev === 'dark' ? 'light' : 'dark')),
    []
  );

  const theme = useMemo(() => buildTheme(mode), [mode]);
  const value = useMemo(
    () => ({ mode, toggleMode, setMode }),
    [mode, toggleMode, setMode]
  );

  return (
    <ThemeModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </ThemeModeContext.Provider>
  );
};

export const useThemeMode = (): ThemeModeContextProps => {
  const context = useContext(ThemeModeContext);
  if (!context) {
    throw new Error('useThemeMode must be used within a ThemeModeProvider');
  }
  return context;
};
